import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "./ThemeToggle";

const navItems = [
  { label: "Our Story", path: "/our-story" },
  { label: "Team", path: "/team" },
  { label: "Services", path: "/services" },
  { label: "Insights", path: "/news" },
  { label: "Locations", path: "/locations" },
  { label: "Client Intake", path: "/intake" },
  { label: "Join Us", path: "/join-us" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  return (
    <nav aria-label="Main navigation" className="fixed top-10 left-0 right-0 z-50 transition-all duration-300 bg-background/80 backdrop-blur-md overflow-visible">
      <div className="flex items-center justify-between px-8 lg:px-16 h-36">
        {/* Logo (left side) */}
        <Link to="/" className="flex shrink-0 items-center" aria-label="Vaughan McLean home">
          <img
            src="/logo-header.png"
            alt="Vaughan McLean"
            className="h-20 w-auto object-contain sm:h-24 lg:h-16 xl:h-24"
          />
        </Link>

        {/* Desktop Nav Links (right side) */}
        <div className="hidden xl:flex items-center gap-8">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={item.label === "Client Intake" 
                ? "px-4 py-2 bg-primary text-primary-foreground font-body text-xs tracking-[0.15em] uppercase hover:bg-primary/90 transition-colors"
                : `nav-link ${location.pathname === item.path ? "gold-text" : ""}`
              }
            >
              {item.label}
            </Link>
          ))}
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="p-2 border border-border hover:border-primary transition-colors"
            aria-label="Search"
          >
            <Search className="w-4 h-4 text-foreground" />
          </button>
          <ThemeToggle />
        </div>

        {/* Mobile: hamburger right */}
        <button
          className="xl:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <X className="w-6 h-6 text-foreground" />
          ) : (
            <Menu className="w-6 h-6 text-foreground" />
          )}
        </button>
      </div>

      {/* Search Bar */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-border"
          >
            <div className="px-8 lg:px-16 py-4">
              <label htmlFor="site-search" className="sr-only">Search the site</label>
              <input
                id="site-search"
                type="text"
                placeholder="Search..."
                className="w-full bg-transparent border-b border-border text-foreground font-body text-sm py-2 outline-none focus:border-primary transition-colors"
                autoFocus
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="xl:hidden overflow-hidden bg-background border-t border-border"
          >
            <div className="flex flex-col px-8 py-6 gap-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link text-sm ${
                    location.pathname === item.path ? "gold-text" : ""
                  }`}
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-2">
                <ThemeToggle />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
